/**
 * Payment receipts — Phase 3 Charges.
 *
 * Lists receipts issued against paid maintenance bills. Each receipt has a
 * tenant-scoped sequenced number (`SOC/2026/06/0001`) + a generated PDF.
 *
 * Admin actions:
 *  - View PDF (opens in new tab via the auth-gated /receipts/:id/pdf endpoint)
 *  - Regenerate PDF (force-rebuild after edits)
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { get, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';

type PaymentMethod = 'razorpay' | 'upi' | 'cash' | 'cheque' | 'neft' | 'manual' | 'card';

interface Receipt {
  id: string;
  bill_id: string;
  unit_id: string;
  receipt_number: string;
  receipt_date: string;
  amount_inr: number;
  payment_method: PaymentMethod;
  payment_ref: string | null;
  notes: string | null;
  pdf_storage_key: string | null;
  unit?: { id: string; number: string };
  bill?: { id: string; period_label: string; amount_inr: number };
}

interface ListResponse {
  items: Receipt[];
  next_cursor?: string | null;
}

const PAGE_SIZE = 50;

const METHOD_LABEL: Record<PaymentMethod, string> = {
  razorpay: 'Razorpay',
  upi: 'UPI',
  cash: 'Cash',
  cheque: 'Cheque',
  neft: 'NEFT',
  manual: 'Manual',
  card: 'Card'
};

function inr(n: number): string {
  return `₹${(n ?? 0).toLocaleString('en-IN')}`;
}

export function ReceiptsPage(): JSX.Element {
  const qc = useQueryClient();
  const [methodFilter, setMethodFilter] = useState<PaymentMethod | ''>('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const filtersHash = `${methodFilter}|${search.trim()}|${from}|${to}`;
  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);

  const listQuery = useQuery({
    queryKey: ['receipts', filtersHash, cursor],
    queryFn: () =>
      get<ListResponse>('/receipts', {
        params: {
          limit: PAGE_SIZE,
          ...(methodFilter ? { payment_method: methodFilter } : {}),
          ...(search.trim() ? { q: search.trim() } : {}),
          ...(from ? { from: new Date(from).toISOString() } : {}),
          ...(to ? { to: new Date(to).toISOString() } : {}),
          ...(cursor ? { cursor } : {})
        }
      }),
    placeholderData: (prev) => prev
  });
  const paginator = useCursorAccumulator<Receipt>(listQuery.data, cursor, filtersHash);
  const items = paginator.items;

  const regeneratePdf = useMutation({
    mutationFn: (id: string) => post<{ ok: true }>(`/receipts/${id}/regenerate-pdf`, {}),
    onSuccess: () => {
      toast.success('Receipt PDF regenerated');
      void qc.invalidateQueries({ queryKey: ['receipts'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const openPdf = (id: string): void => {
    // The PDF endpoint requires a bearer token. We open it via fetch and
    // create a blob URL so the auth header gets attached.
    void (async () => {
      try {
        const blob = await get<Blob>(`/receipts/${id}/pdf`, {
          responseType: 'blob' as never
        } as never);
        const url = URL.createObjectURL(blob as unknown as Blob);
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (e) {
        toast.error(getErrorMessage(e));
      }
    })();
  };

  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="Payment receipts"
        description="Tax invoices issued against paid maintenance bills. PDF copies are cached after first generation."
      />

      <Card>
        <CardContent className="p-vt-3 flex flex-wrap items-end gap-vt-2">
          <div className="flex min-w-[200px] flex-col gap-vt-1">
            <Label className="text-vt-12 text-vt-text-muted">Search receipt #</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-vt-text-muted" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="SOC/2026/06/0001"
                className="pl-8"
              />
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label className="text-vt-12 text-vt-text-muted">Method</Label>
            <Select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value as PaymentMethod | '')}>
              <option value="">All</option>
              {Object.keys(METHOD_LABEL).map((m) => (
                <option key={m} value={m}>
                  {METHOD_LABEL[m as PaymentMethod]}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label className="text-vt-12 text-vt-text-muted">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label className="text-vt-12 text-vt-text-muted">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-vt-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<FileText className="h-8 w-8" strokeWidth={1.5} />}
              title="No receipts yet"
              description="Receipts are auto-generated when admin records a payment against a bill. Try recording one from Bills."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Receipt #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Ref</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-vt-12">{r.receipt_number}</TableCell>
                    <TableCell className="text-vt-12 text-vt-text-muted">
                      {new Date(r.receipt_date).toLocaleDateString('en-IN')}
                    </TableCell>
                    <TableCell className="font-medium">{r.unit?.number ?? '—'}</TableCell>
                    <TableCell className="text-vt-12">{r.bill?.period_label ?? '—'}</TableCell>
                    <TableCell className="text-right font-mono text-vt-13">{inr(r.amount_inr)}</TableCell>
                    <TableCell>
                      <Badge variant="info">{METHOD_LABEL[r.payment_method] ?? r.payment_method}</Badge>
                    </TableCell>
                    <TableCell className="text-vt-12 text-vt-text-muted">{r.payment_ref ?? '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-vt-1">
                        <Button size="sm" variant="secondary" onClick={() => openPdf(r.id)}>
                          <FileText className="h-3 w-3" /> View PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => regeneratePdf.mutate(r.id)}
                          disabled={regeneratePdf.isPending}
                        >
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="p-vt-3">
            <CursorPaginator
              hasMore={Boolean(paginator.nextCursor)}
              loading={listQuery.isFetching}
              onLoadMore={() => paginator.nextCursor && setCursor(paginator.nextCursor)}
              itemsShown={items.length}
              pageSize={PAGE_SIZE}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
